import React from 'react';
import ReactPaginate from 'react-paginate';
import { Badge } from '../ForBrands/ui/BrandUI';

// One status map for the Payments tabs (Payment History, Invoice Management
// and Manage Tiers). Paid, success and active read as an outline Badge with
// the bm-positive dot, pending and canceling as attention, failed, overdue,
// canceled and past due as critical, and everything else as passive.

// bm-positive in tailwind.config.js.
export const POSITIVE_DOT = '#1f9d55';

const POSITIVE = { tone: 'outline', dot: POSITIVE_DOT };
const ATTENTION = { tone: 'attention' };
const CRITICAL = { tone: 'critical' };
const PASSIVE = { tone: 'passive' };
// In flight but not settled (a sent invoice, a trial): a plain outline.
const NEUTRAL = { tone: 'outline' };

export const PAYMENT_STATUS = {
  paid: { label: 'Paid', ...POSITIVE },
  success: { label: 'Success', ...POSITIVE },
  active: { label: 'Active', ...POSITIVE },
  pending: { label: 'Pending', ...ATTENTION },
  canceling: { label: 'Canceling', ...ATTENTION },
  // A manual-era active plan with no Stripe or manual flag, flagged for the
  // Reset to free cleanup.
  legacy: { label: 'Active (Legacy)', ...ATTENTION },
  failed: { label: 'Failed', ...CRITICAL },
  overdue: { label: 'Overdue', ...CRITICAL },
  canceled: { label: 'Canceled', ...CRITICAL },
  cancelled: { label: 'Cancelled', ...CRITICAL },
  past_due: { label: 'Past due', ...CRITICAL },
  unpaid: { label: 'Unpaid', ...CRITICAL },
  sent: { label: 'Sent', ...NEUTRAL },
  trialing: { label: 'Trial', ...NEUTRAL },
  draft: { label: 'Draft', ...PASSIVE },
  refunded: { label: 'Refunded', ...PASSIVE },
  expired: { label: 'Expired', ...PASSIVE },
  incomplete: { label: 'Incomplete', ...PASSIVE },
  incomplete_expired: { label: 'Incomplete expired', ...PASSIVE },
  incomplete_refunded: { label: 'Incomplete refunded', ...PASSIVE },
  free: { label: 'Free', ...PASSIVE },
};

const humanize = (value) => {
  const text = String(value || '').replace(/_/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
};

// The Badge tone, dot and label for a status. `label` overrides the map's
// label (Manage Tiers shows "Active (Stripe)" on the active tone).
export const paymentStatusMeta = (status, label) => {
  const key = String(status || '').trim().toLowerCase();
  const meta = PAYMENT_STATUS[key];
  if (meta) return { ...meta, label: label || meta.label };
  return { ...PASSIVE, label: label || humanize(status) || 'Unknown' };
};

export const PaymentStatusBadge = ({ status, label, className }) => {
  const meta = paymentStatusMeta(status, label);
  return (
    <Badge tone={meta.tone} dot={meta.dot} className={className}>
      {meta.label}
    </Badge>
  );
};

// The V2 Users table classes (AdminUserManager): bm-label-xs uppercase
// headers on bg-bm-subtle, border-bm-border rows with a bg-bm-subtle hover
// and bm-body cells.
export const TH_CLASS = 'whitespace-nowrap border-b border-bm-border px-2.5 py-2.5 text-left bm-label-xs uppercase tracking-[0.06em] text-bm-muted';
export const TR_CLASS = 'border-b border-bm-border bg-white transition-colors last:border-0 hover:bg-bm-subtle';
export const TD_CLASS = 'px-2.5 py-2.5 align-middle bm-body-m text-bm-ink';
export const TD_MUTED_CLASS = 'px-2.5 py-2.5 align-middle bm-body-m text-bm-muted';

// A solid critical confirm button (the ConfirmModal danger style) for the
// type-the-email dialogs, grey while it is disabled.
export const DANGER_SOLID = '!bg-bm-critical hover:!bg-[#c41d14] disabled:!bg-bm-border disabled:!text-bm-muted';

// ReactPaginate with the Users page class set. `page` is 1-based. The list
// wraps so a long pager never runs past a phone-width card.
export const PaymentsPager = ({ page, pageCount, onChange }) => {
  if (!pageCount || pageCount <= 1) return null;
  return (
    <ReactPaginate
      previousLabel="← Prev"
      nextLabel="Next →"
      breakLabel="..."
      pageCount={pageCount}
      marginPagesDisplayed={1}
      pageRangeDisplayed={3}
      onPageChange={({ selected }) => onChange(selected + 1)}
      containerClassName="flex max-w-full flex-wrap items-center gap-1 bm-label-xs"
      pageClassName="rounded-full border border-bm-border"
      pageLinkClassName="inline-flex h-8 min-w-[32px] items-center justify-center px-2 text-bm-ink"
      activeClassName="!border-bm-n700 bg-bm-n700 text-white"
      activeLinkClassName="text-white"
      previousClassName="rounded-full border border-bm-border"
      previousLinkClassName="inline-flex h-8 items-center justify-center px-3 text-bm-ink"
      nextClassName="rounded-full border border-bm-border"
      nextLinkClassName="inline-flex h-8 items-center justify-center px-3 text-bm-ink"
      breakLinkClassName="inline-flex h-8 items-center justify-center px-2 text-bm-tertiary"
      disabledClassName="opacity-40 cursor-not-allowed"
      forcePage={Math.min(Math.max(page - 1, 0), pageCount - 1)}
    />
  );
};
